import { graphql, fetchQuery, type Environment } from 'react-relay';
import { OPERAND_TYPE_FUNCTION, OPERAND_TYPE_VALUE } from '@atlaskit/jql-ast';
import { expVal } from '@atlassian/jira-feature-experiments';
import type {
	EndCursor,
	Option,
	AvatarOption,
	OptionResponse,
	OptionGroup,
	SearchTemplate,
} from '@atlassian/jira-jql-builder-basic-picker/src/common/types.tsx';
import type { FormatMessage } from '@atlassian/jira-shared-types/src/general.tsx';
import { OperandValues } from '../../../../../../common/constants.tsx';
import type { PropsResolver, PropTypes } from '../../../../../../common/types.tsx';
import {
	generateOptions,
	generateErrorOptions,
	fireJqlBuilderBasicErrorAnalytics,
} from '../../../../../../common/utils.tsx';
import { getFunctionLabel } from '../../../../../../utils/get-function-label/index.tsx';
import type { getIssueTypeProps_initialOptionsQuery } from './__generated__/getIssueTypeProps_initialOptionsQuery.graphql';
import type {
	getIssueTypePropsQuery,
	getIssueTypePropsQuery$data as issueTypePropsResolver$data,
	JiraJqlViewContext,
	JiraJqlScopeInput,
} from './__generated__/getIssueTypePropsQuery.graphql';
import messages from './messages.tsx';

type CloudId = string;
type Params = {
	fieldId: string;
	propType: PropTypes;
	formatMessage: FormatMessage;
	jql?: string;
	environment: Environment;
	cloudId: CloudId;
	operandDisplayValues?: string[];
	searchTemplate: SearchTemplate;
	viewContext: JiraJqlViewContext | null;
};

type GetInitialParams = {
	environment: Environment;
	cloudId: CloudId;
	jql?: string;
	formatMessage: FormatMessage;
};

/*
    On picker search, we also need to know JQL and search parameters
*/
type GetFieldValueParams = {
	environment: Environment;
	cloudId: CloudId;
	lowerCaseFieldId: string;
	jql?: string;
	search: string;
	endCursor: EndCursor;
	viewContext: JiraJqlViewContext | null;
	scope?: JiraJqlScopeInput | null;
};

type IssueTypeNode = NonNullable<
	NonNullable<
		NonNullable<
			NonNullable<
				NonNullable<NonNullable<issueTypePropsResolver$data['jira']>['jqlBuilder']>['fieldValues']
			>['edges']
		>[number]
	>['node']
>;

const getFirstIssueTypeAvatar = (node: IssueTypeNode) =>
	node.issueTypes?.find(
		(issueType: {
			readonly avatar?: {
				readonly xsmall: string | null | undefined;
			} | null;
		}) => typeof issueType.avatar?.xsmall === 'string' && issueType.avatar.xsmall.length > 0,
	)?.avatar?.xsmall;

export const getOption = (node: IssueTypeNode, itemType: 'value'): Option | AvatarOption => {
	const { displayName, jqlTerm } = node;

	const option: Option = {
		label: displayName,
		value: jqlTerm,
		optionType: 'option',
		itemType,
	};

	if (node.issueTypes) {
		return {
			...option,
			optionType: 'avatar',
			avatar: getFirstIssueTypeAvatar(node),
			square: true,
			itemType,
		};
	}

	return option;
};

const getInitialOptions = async ({
	environment,
	cloudId,
	jql,
	formatMessage,
}: GetInitialParams): Promise<OptionResponse> => {
	const data = await fetchQuery<getIssueTypeProps_initialOptionsQuery>(
		environment,
		graphql`
			query getIssueTypeProps_initialOptionsQuery($cloudId: ID!, $jql: String!) {
				jira @required(action: THROW) {
					jqlBuilder(cloudId: $cloudId) {
						issueTypes(jqlContext: $jql) {
							aboveBaseLevel(first: 2) {
								edges {
									node {
										jqlTerm
										displayName
										issueTypes {
											avatar {
												xsmall
											}
										}
									}
								}
							}
							baseLevel(first: 3) {
								edges {
									node {
										jqlTerm
										displayName
										issueTypes {
											avatar {
												xsmall
											}
										}
									}
								}
							}
							belowBaseLevel(first: 5) {
								edges {
									node {
										jqlTerm
										displayName
										issueTypes {
											avatar {
												xsmall
											}
										}
									}
								}
							}
						}
					}
				}
			}
		`,
		{
			cloudId,
			jql: jql || '',
		},
		{ fetchPolicy: 'store-or-network' },
	).toPromise();
	const issueTypes = data?.jira?.jqlBuilder?.issueTypes;
	if (!issueTypes) {
		return generateOptions([]);
	}

	const aboveBaseLevelTypes = issueTypes?.aboveBaseLevel?.edges || [];
	const baseLevelTypes = issueTypes?.baseLevel?.edges || [];
	const belowBaseLevelTypes = issueTypes?.belowBaseLevel?.edges || [];

	const standardTypes = aboveBaseLevelTypes
		.concat(baseLevelTypes)
		.reduce<Array<Option | AvatarOption>>((options, edges) => {
			if (edges?.node === undefined || edges?.node === null) {
				return options;
			}

			const { node } = edges;
			if (node !== undefined && node !== null) {
				options.push(getOption(node, OPERAND_TYPE_VALUE));
			}

			return options;
		}, []);
	const subtaskTypes = belowBaseLevelTypes.reduce<Array<Option | AvatarOption>>(
		(options, edges) => {
			if (edges?.node === undefined || edges?.node === null) {
				return options;
			}

			const { node } = edges;
			if (node !== undefined && node !== null) {
				options.push(getOption(node, OPERAND_TYPE_VALUE));
			}

			return options;
		},
		[],
	);

	const standardGroupOption: OptionGroup[] = [
		{
			label: formatMessage(
				expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
					? messages.refinementSubtitleStandardIssueTypesIssueTermRefresh
					: messages.refinementSubtitleStandardIssueTypes,
			),
			optionType: 'group',
			options: standardTypes,
		},
	];

	const subtaskGroupOption: OptionGroup[] = [
		{
			label: formatMessage(
				expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
					? messages.refinementSubtitleSubTaskIssueTypesIssueTermRefresh
					: messages.refinementSubtitleSubTaskIssueTypes,
			),
			optionType: 'group',
			options: subtaskTypes,
		},
	];

	return generateOptions([
		{
			label: getFunctionLabel('standardIssueTypes', formatMessage),
			value: OperandValues.standardIssueTypes.value,
			optionType: 'avatar',
			hideAvatar: true,
			itemType: OPERAND_TYPE_FUNCTION,
		},
		{
			label: getFunctionLabel('subTaskIssueTypes', formatMessage),
			value: OperandValues.subTaskIssueTypes.value,
			optionType: 'avatar',
			hideAvatar: true,
			itemType: OPERAND_TYPE_FUNCTION,
		},
		...(Array.isArray(standardTypes) && standardTypes.length > 0 ? standardGroupOption : []),
		...(Array.isArray(subtaskTypes) && subtaskTypes.length > 0 ? subtaskGroupOption : []),
	]);
};

const getFieldValueOptions = async ({
	environment,
	cloudId,
	lowerCaseFieldId,
	jql,
	search,
	endCursor,
	viewContext,
	scope = null,
}: GetFieldValueParams): Promise<OptionResponse> => {
	const after = endCursor;

	const data = await fetchQuery<getIssueTypePropsQuery>(
		environment,
		graphql`
			query getIssueTypePropsQuery(
				$cloudId: ID!
				$first: Int = 10
				$jqlTerm: String!
				$jql: String!
				$searchString: String!
				$after: String
				$viewContext: JiraJqlViewContext
				$scope: JiraJqlScopeInput
			) {
				jira @required(action: THROW) {
					jqlBuilder(cloudId: $cloudId) {
						fieldValues(
							first: $first
							jqlTerm: $jqlTerm
							jqlContext: $jql
							searchString: $searchString
							after: $after
							viewContext: $viewContext
							scope: $scope
						) {
							totalCount
							pageInfo {
								endCursor
							}
							edges {
								node {
									jqlTerm
									displayName
									... on JiraJqlIssueTypeFieldValue {
										issueTypes {
											avatar {
												xsmall
											}
										}
									}
								}
							}
						}
					}
				}
			}
		`,
		{
			cloudId,
			first: 10,
			jqlTerm: lowerCaseFieldId,
			jql: jql || '',
			searchString: search,
			after,
			viewContext,
			scope,
		},
		{ fetchPolicy: 'store-or-network' },
	).toPromise();
	if (!data?.jira?.jqlBuilder?.fieldValues?.edges) {
		return generateOptions([]);
	}

	const { edges: fieldValues, totalCount, pageInfo } = data.jira.jqlBuilder.fieldValues;
	return generateOptions(
		fieldValues.reduce<Array<Option | AvatarOption>>((options, edges) => {
			if (!edges || !edges.node) {
				return options;
			}

			const { node } = edges;
			if (node !== undefined && node !== null) {
				options.push(getOption(node, OPERAND_TYPE_VALUE));
			}

			return options;
		}, []),
		totalCount,
		pageInfo.endCursor || undefined,
	);
};

export const getIssueTypeProps = ({
	fieldId,
	formatMessage,
	jql,
	environment,
	cloudId,
	operandDisplayValues,
	searchTemplate,
	viewContext,
}: Params): PropsResolver => ({
	enableFullList: true,
	operandDisplayValues,
	loadOptions: async (search, renderInitialValues, endCursor) => {
		try {
			if (search.length === 0 && renderInitialValues) {
				return await getInitialOptions({
					environment,
					cloudId,
					jql,
					formatMessage,
				});
			}
			return await getFieldValueOptions({
				environment,
				cloudId,
				lowerCaseFieldId: fieldId.toLowerCase(),
				jql,
				search,
				endCursor,
				viewContext,
			});
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			fireJqlBuilderBasicErrorAnalytics(
				'issueTypeJqlBuilderBasicFieldValues',
				searchTemplate,
				error,
			);
			return generateErrorOptions(error);
		}
	},
});
